import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Decision, WebDeviceTopic } from "../api/consent";

/** localStorage key: the device's own answers (strictly necessary storage — it remembers the choice itself). */
export const CONSENT_STORE_KEY = "consent-device";

export interface DeviceAnswer {
  versionId: string;
  decision: Decision;
}

/** The two gate flags every iam token response carries (sign-in, refresh, set-password, profile setup). */
export interface AuthConsentFlags {
  consentPending?: boolean;
  birthDatePending?: boolean;
}

interface ConsentState {
  device: Partial<Record<WebDeviceTopic, DeviceAnswer>>;
  /** A gate topic (terms / privacy notice) still needs an answer: the app is blocked by the gate. */
  consentPending: boolean;
  /** No date of birth is stored for the signed-in account: the Date of birth screen comes first. */
  birthDatePending: boolean;
  /** A consent dialog (Before you start, re-consent, date of birth) is open: the storage banner stays out of its way. */
  gateOpen: boolean;
  setDevice(key: WebDeviceTopic, answer: DeviceAnswer): void;
  /** Forgets an answer that no longer counts (e.g. a new version of the topic needs an answer). */
  clearDevice(key: WebDeviceTopic): void;
  setPending(pending: boolean): void;
  setBirthDatePending(pending: boolean): void;
  /** Stores the flags of a token response; a missing flag (old server) means false. */
  setAuthFlags(flags: AuthConsentFlags): void;
  setGateOpen(open: boolean): void;
  isAccepted(key: WebDeviceTopic): boolean;
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      device: {},
      consentPending: false,
      birthDatePending: false,
      gateOpen: false,
      setDevice: (key, answer) => set((s) => ({ device: { ...s.device, [key]: answer } })),
      clearDevice: (key) =>
        set((s) => {
          const device = { ...s.device };
          delete device[key];
          return { device };
        }),
      setPending: (consentPending) => set({ consentPending }),
      setBirthDatePending: (birthDatePending) => set({ birthDatePending }),
      setAuthFlags: (flags) =>
        set({
          consentPending: flags.consentPending === true,
          birthDatePending: flags.birthDatePending === true,
        }),
      setGateOpen: (gateOpen) => set({ gateOpen }),
      isAccepted: (key) => get().device[key]?.decision === "ACCEPTED",
    }),
    {
      name: CONSENT_STORE_KEY,
      // The flags survive a reload so the gate shows again before the app (never the date itself, only "pending").
      partialize: (s) => ({
        device: s.device,
        consentPending: s.consentPending,
        birthDatePending: s.birthDatePending,
      }),
    },
  ),
);

/** Neither a date of birth nor a consent answer is owed: the app's connections (PubNub, RabbitMQ, push) may start. */
export function isConsentGateClear(): boolean {
  const s = useConsentStore.getState();
  return !s.consentPending && !s.birthDatePending;
}
